import React from 'react';
import { Microphone, Sparkle, Plus } from '@phosphor-icons/react';

const TASKS = ['Frame the north wall', 'Clean up all debris', 'Check plumbing rough-in'];

/* 9s looping CSS-only animation: mic listens → transcript reveals → AI splits → tasks pop in */
const CSS = `
.vtm-listen { animation: vtmListen 9s infinite; }
@keyframes vtmListen { 0%, 34% { opacity: 1; } 40%, 96% { opacity: .3; } 100% { opacity: 1; } }

.vtm-pulse { animation: vtmPulse 1.2s ease-out infinite; }
@keyframes vtmPulse { 0% { transform: scale(1); opacity: .6; } 100% { transform: scale(1.9); opacity: 0; } }

.vtm-bar { transform-origin: bottom; animation: vtmBar .7s ease-in-out infinite alternate; }
.vtm-bar-1 { animation-delay: .12s; }
.vtm-bar-2 { animation-delay: .24s; }
.vtm-bar-3 { animation-delay: .36s; }
@keyframes vtmBar { from { transform: scaleY(.25); } to { transform: scaleY(1); } }

.vtm-reveal { animation: vtmReveal 9s linear infinite; }
@keyframes vtmReveal {
  0%, 2% { clip-path: inset(0 100% 0 0); }
  32% { clip-path: inset(0 0 0 0); }
  96% { clip-path: inset(0 0 0 0); }
  100% { clip-path: inset(0 100% 0 0); }
}

.vtm-split { opacity: 0; animation: vtmSplit 9s infinite; }
@keyframes vtmSplit {
  0%, 34% { opacity: 0; transform: scale(.85); }
  38%, 52% { opacity: 1; transform: scale(1); }
  56%, 100% { opacity: 0; transform: scale(.85); }
}

.vtm-done { opacity: 0; animation: vtmDone 9s infinite; }
@keyframes vtmDone {
  0%, 64% { opacity: 0; transform: translateY(4px); }
  68%, 94% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; }
}

.vtm-task-1 { opacity: 0; animation: vtmTask1 9s infinite; }
.vtm-task-2 { opacity: 0; animation: vtmTask2 9s infinite; }
.vtm-task-3 { opacity: 0; animation: vtmTask3 9s infinite; }
@keyframes vtmTask1 { 0%, 40% { opacity: 0; transform: translateX(-10px); } 45%, 94% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; } }
@keyframes vtmTask2 { 0%, 48% { opacity: 0; transform: translateX(-10px); } 53%, 94% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; } }
@keyframes vtmTask3 { 0%, 56% { opacity: 0; transform: translateX(-10px); } 61%, 94% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; } }

@media (prefers-reduced-motion: reduce) {
  .vtm-listen, .vtm-pulse, .vtm-bar, .vtm-reveal, .vtm-split, .vtm-done,
  .vtm-task-1, .vtm-task-2, .vtm-task-3 { animation: none !important; opacity: 1 !important; clip-path: none !important; transform: none !important; }
}
`;

export const VoiceTaskSplitMock = ({ className = '' }) => (
  <div
    className={`relative bg-white dark:bg-neutral-900 border-2 border-black p-4 ${className}`}
    data-testid="voice-task-split-mock"
  >
    <style>{CSS}</style>

    <div className="flex items-center justify-between mb-3">
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#D95800]">
        <Microphone size={12} weight="fill" /> Voice → Tasks
      </span>
      <span className="text-[9px] font-black uppercase tracking-widest bg-black text-white px-1.5 py-0.5 border border-[#D95800]">
        EN / ES
      </span>
    </div>

    {/* Listening row */}
    <div className="vtm-listen flex items-center gap-3 mb-2">
      <div className="relative flex-shrink-0 w-9 h-9">
        <span className="vtm-pulse absolute inset-0 rounded-full bg-red-500" />
        <div className="relative w-9 h-9 bg-red-600 border-2 border-black flex items-center justify-center">
          <Microphone size={16} weight="fill" className="text-white" />
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-6" aria-hidden="true">
        {[0, 1, 2, 3, 0, 1, 2, 3].map((d, i) => (
          <span key={i} className={`vtm-bar vtm-bar-${d} w-[3px] h-full bg-[#D95800]`} />
        ))}
      </div>
      <span className="text-[9px] font-black uppercase tracking-widest text-red-600">● REC</span>
    </div>

    {/* Transcript */}
    <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 p-2 mb-3 min-h-[3.4rem]">
      <span className="vtm-reveal block text-xs italic text-neutral-600 dark:text-neutral-300 leading-snug">
        “Frame the north wall, then clean up the debris, then check the plumbing rough-in…”
      </span>
    </div>

    {/* AI split + done badges */}
    <div className="relative h-6 mb-2">
      <span className="vtm-split absolute left-0 top-0 inline-flex items-center gap-1 bg-black text-[#D95800] text-[9px] font-black uppercase tracking-widest px-2 py-1 border-2 border-[#D95800]">
        <Sparkle size={10} weight="fill" /> AI splitting…
      </span>
      <span className="vtm-done absolute right-0 top-0 inline-flex items-center gap-1 bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest px-2 py-1 border-2 border-black">
        3 tasks added
      </span>
    </div>

    {/* Split tasks */}
    <ul className="space-y-1.5">
      {TASKS.map((t, i) => (
        <li
          key={t}
          className={`vtm-task-${i + 1} flex items-center gap-2 p-2 bg-[#E8E9EB] dark:bg-neutral-800 border-2 border-black`}
        >
          <span className="w-4 h-4 flex-shrink-0 bg-[#D95800] border-2 border-black flex items-center justify-center">
            <Plus size={10} weight="bold" className="text-black" />
          </span>
          <span className="text-xs font-bold text-neutral-800 dark:text-neutral-100">{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default VoiceTaskSplitMock;
